import { useState } from "react";
import { CheckCircle2, Edit2, Save, X } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ProveedorSelector } from "./ProveedorSelector";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import { toast } from "react-hot-toast";

interface ItemActividadRowProps {
  actividad: any;
  otEstado: string;
  onUpdate: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function ItemActividadRow({
  actividad,
  otEstado,
  onUpdate,
  isSelected = false,
  onSelect,
  showPrice = true, // Default to true
  customBadge,      // Optional badge
}: ItemActividadRowProps & { showPrice?: boolean; customBadge?: React.ReactNode }) {
  const [editando, setEditando] = useState(false);
  const [proveedorId, setProveedorId] = useState(
    actividad.proveedor_seleccionado_id,
  );
  const [precioCotizado, setPrecioCotizado] = useState(
    actividad.precio_cotizado || actividad.costo_unitario,
  );
  const [guardando, setGuardando] = useState(false);

  const handleProveedorSelect = (
    newProveedorId: number,
    nuevoPrecio: number,
  ) => {
    setProveedorId(newProveedorId);
    setPrecioCotizado(nuevoPrecio);
  };

  const handleSave = async () => {
    try {
      setGuardando(true);
      await maintenanceService.updateActividadOT(actividad.id, {
        proveedor_seleccionado_id: proveedorId,
        precio_cotizado: precioCotizado,
        costo_unitario: precioCotizado,
      });
      toast.success("Proveedor actualizado");
      setEditando(false);
      onUpdate();
    } catch (error) {
      toast.error("Error al actualizar proveedor");
      console.error(error);
    } finally {
      setGuardando(false);
    }
  };

  const handleToggleRealizado = async () => {
    if (otEstado !== "EN_PROGRESO") {
      toast.error("Solo se pueden marcar actividades en órdenes EN PROCESO");
      return;
    }

    try {
      await maintenanceService.updateActividadOT(actividad.id, {
        realizado: !actividad.realizado,
      });
      toast.success(
        actividad.realizado
          ? "Actividad desmarcada"
          : "Actividad marcada como realizada",
      );
      onUpdate();
    } catch (error) {
      toast.error("Error al actualizar actividad");
      console.error(error);
    }
  };

  const tieneProveedor = actividad.proveedor_seleccionado_id || proveedorId;
  const precioFinal = precioCotizado || actividad.costo_unitario;
  const precioOriginal =
    actividad.item_catalogo?.precio_unitario || actividad.costo_unitario;
  const hayDescuento = precioFinal < precioOriginal;

  return (
    <div
      className={`flex items-center p-3 rounded-lg border transition-all group ${actividad.realizado
        ? "bg-green-50 border-green-200"
        : "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50"
        }`}
    >
      {/* Checkbox de selección (Sourcing) */}
      <div
        className={`h-5 w-5 rounded border-2 mr-3 flex items-center justify-center cursor-pointer transition-all ${isSelected
          ? "border-indigo-500 bg-indigo-500 shadow-sm"
          : "border-gray-200 hover:border-indigo-300 bg-white"
          }`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.();
        }}
      >
        {isSelected && (
          <div className="h-2 w-2 bg-white rounded-full scale-110 animate-in zoom-in-50 duration-200" />
        )}
      </div>

      {/* Checkbox de realización */}
      <div
        className={`h-5 w-5 rounded border-2 mr-3 flex items-center justify-center cursor-pointer transition-all ${otEstado === "EN_PROGRESO"
          ? "hover:border-blue-500"
          : "opacity-50 cursor-not-allowed"
          } ${actividad.realizado
            ? "border-green-500 bg-green-500"
            : "border-gray-300"
          }`}
        onClick={handleToggleRealizado}
      >
        {actividad.realizado && <CheckCircle2 className="h-5 w-5 text-white" />}
      </div>

      {/* Contenido principal */}
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-800">
                {actividad.item_catalogo?.nombre || "Ítem desconocido"}
              </p>
              {customBadge}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
              <span>
                {actividad.cantidad} {actividad.item_catalogo?.unidad_medida}
              </span>

              {/* Mostrar proveedor o botón para seleccionar */}
              {editando ? (
                <div className="flex items-center gap-2">
                  <span>•</span>
                  <ProveedorSelector
                    itemId={actividad.item_catalogo_id}
                    itemNombre={actividad.item_catalogo?.nombre || ""}
                    onSelect={handleProveedorSelect}
                    proveedorPreseleccionado={proveedorId}
                  />
                </div>
              ) : tieneProveedor ? (
                <>
                  <span>•</span>
                  <span className="font-medium text-blue-600">
                    {actividad.proveedor_seleccionado?.razon_social ||
                      "Proveedor seleccionado"}
                  </span>
                  {hayDescuento && (
                    <Badge className="bg-green-100 text-green-700 text-[10px] h-4">
                      Ahorro
                    </Badge>
                  )}
                </>
              ) : (
                <>
                  <span>•</span>
                  <span className="text-orange-600 font-medium">
                    Sin proveedor
                  </span>
                </>
              )}
            </div>
            {actividad.observaciones && !actividad.observaciones.startsWith("Postergada") && !actividad.observaciones.startsWith("Cancelada") && (
              <p className="text-[10px] text-gray-400 mt-1 italic font-medium leading-tight">
                Nota: {actividad.observaciones}
              </p>
            )}
          </div>

          {/* Precio y acciones */}
          <div className="flex items-center gap-2">
            {showPrice && (
              <div className="text-right flex flex-col items-end">
                <span className="text-sm font-bold text-gray-600">
                  S/ {(Number(precioFinal) * Number(actividad.cantidad)).toFixed(2)}
                </span>
                {(Number(actividad.cantidad) > 1 || Number(actividad.cantidad) !== 1) && (
                  <span className="text-[10px] text-gray-500">
                    {Number(actividad.cantidad)} x S/ {Number(precioFinal).toFixed(2)}
                  </span>
                )}
                {hayDescuento && (
                  <p className="text-xs text-gray-400 line-through">
                    S/ {(Number(precioOriginal) * Number(actividad.cantidad)).toFixed(2)}
                  </p>
                )}
              </div>
            )}

            {/* Botones de edición (solo si está ABIERTA o EN_PROGRESO) */}
            {(otEstado === "ABIERTA" || otEstado === "EN_PROGRESO") && (
              <div className="flex gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                {editando ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-green-50"
                      onClick={handleSave}
                      disabled={guardando}
                    >
                      <Save className="h-3.5 w-3.5 text-green-600" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-slate-50"
                      onClick={() => {
                        setEditando(false);
                        setProveedorId(actividad.proveedor_seleccionado_id);
                        setPrecioCotizado(
                          actividad.precio_cotizado || actividad.costo_unitario,
                        );
                      }}
                    >
                      <X className="h-3.5 w-3.5 text-slate-600" />
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 hover:bg-blue-50"
                    onClick={() => setEditando(true)}
                    title="Asignar proveedor individualmente"
                  >
                    <Edit2 className="h-3.5 w-3.5 text-blue-600" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
